"use client"

import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";

/**
 * Subscription status banner (docs/codebase/subscription/README.md §8.2).
 *
 * Reads GET /api/v1/company/license (Redis-cached on the shard) and renders
 * the shared escalation ladder: info (≤10 days left) → warning (≤3 days) →
 * error (grace period) → blocker (read-only). Levels error/blocker are
 * persistent; info/warning can be dismissed for the session.
 */

type LicenseSummary = {
  status: string;
  functional: boolean;
  seats_licensed: number;
  seats_used: number;
  period_end: string | null;
  days_left: number | null;
  banner: "none" | "info" | "warning" | "error" | "blocker";
  billing_url: string;
};

const STYLES: Record<string, string> = {
  info: "bg-blue-50 text-blue-800 border-blue-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  error: "bg-red-50 text-red-800 border-red-200",
  blocker: "bg-red-900 text-white border-red-900",
};

const DISMISS_KEY = "license-banner-dismissed";

export function LicenseBanner() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = React.useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );

  const { data } = useQuery<LicenseSummary>({
    queryKey: ["company-license"],
    queryFn: async () => (await apiClient.get("/api/v1/company/license")).data,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    retry: 1,
  });

  if (!data || data.banner === "none") return null;
  const dismissible = data.banner === "info" || data.banner === "warning";
  if (dismissible && dismissed) return null;

  let message: string;
  if (data.banner === "blocker") {
    message = t("license.banner_blocker",
      "Your subscription has expired — the account is read-only and online booking is paused. Renew to restore everything instantly.");
  } else if (data.banner === "error") {
    message = t("license.banner_grace",
      "Your plan has ended. Everything still works for a few more days — renew now to avoid read-only mode.");
  } else if (data.status === "trialing") {
    message = t("license.banner_trial",
      "Your free trial ends in {{days}} days.", { days: data.days_left ?? 0 });
  } else {
    message = t("license.banner_renewal",
      "Your subscription ends in {{days}} days.", { days: data.days_left ?? 0 });
  }

  return (
    <div className={`flex items-center gap-3 border-b px-4 py-2 text-sm ${STYLES[data.banner]}`}
         data-testid="license-banner">
      <span className="flex-1">{message}</span>
      <a
        href={data.billing_url}
        target="_blank"
        rel="noopener noreferrer"
        className={`shrink-0 rounded-md px-3 py-1 font-semibold no-underline ${
          data.banner === "blocker"
            ? "bg-white text-red-900"
            : "bg-rose-600 text-white"
        }`}
      >
        {data.status === "trialing"
          ? t("license.banner_cta_trial", "Choose a plan")
          : t("license.banner_cta_renew", "Renew")}
      </a>
      {dismissible && (
        <button
          type="button"
          aria-label={t("common.dismiss", "Dismiss")}
          className="shrink-0 opacity-60 hover:opacity-100"
          onClick={() => {
            sessionStorage.setItem(DISMISS_KEY, "1");
            setDismissed(true);
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
